import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { CarouselProvider, Slider, Slide, DotGroup, ButtonNext } from 'pure-react-carousel';
import ReactPlayer from 'react-player';
import ExternalLink from '../../Common/ExternalLink/ExternalLink';
import VideoSlide from '../../PoweredByPurpose/VideoSlide/VideoSlide';
import './StoryCarousel.scss';

import { storyCardTypes, mixedMediaTypes, linkTypes } from '../../../models/Models';
import { calculateSize, formatParagraphs, getImageFilepath, getBackground } from '../../../models/Utils';
import { ADAARIARoles, ADALandmarks } from '../../../models/ADA';

const StoryCarousel = ({ cardData: card, isInline, size, forceInlineImage, preventScrolling, onScrollCallback, onScrollCompleteCallback }) => {
  const activeKey = useSelector(state => state.data.activeSlideKey);
  var dimensions = calculateSize(1920, 1080, size.width, size.height);
  const [isActive, setActiveState] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(false);
  const [cardData, setCardData] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [isIdle, setIsIdle] = useState(true);
  const [touchLocX, setTouchLocX] = useState(0);
  const [lastTouchLocX, setLastTouchLocX] = useState(0);
  const menuOpened = useSelector(state => state.data.menuOpened);
  const carouselRef = useRef(null);
  const controlRef = useRef(null);
  const videoRef = useRef(null);
  const videoTimeout = useSelector(state => state.data.videoFadeTimeout);

  useEffect(() => {
    delayedVideoFade();
    setIsIdle(true);

    if (cardData && activeKey === cardData.Key) {
      setActiveState(true);    
    } else {
      setActiveState(false);
    }
  }, [activeKey, cardData]);

  useEffect(() => {
    setCardData(getSlideDataFromCard(card));
  }, [card]);

  function delayedVideoFade() {
    setTimeout(handleVideoStart, videoTimeout);
  }

  function handleCarouselInteraction(){
    setIsIdle(false);
  }

  function handleVideoStart() {
    setLoaded(true);
  }

  function handleNextButtonPress() {
    
  }

  useEffect(() => {
    if (!carouselRef.current || !controlRef.current) {
      return;
    }
    let checkForScroll = true;
    let maxDeltaX = 0;
    const handleScroll = e => {
      if(!isInline){
        e.preventDefault();
      }
      const scrollThreshold = 2;
      if (checkForScroll && Math.abs(e.deltaY) < Math.abs(e.deltaX)) {
        if (Math.abs(e.deltaX) > Math.abs(maxDeltaX) && Math.abs(maxDeltaX) <= scrollThreshold) {
          maxDeltaX = e.deltaX;
        } else {
          checkForScroll = false;
          if (Math.abs(maxDeltaX) > scrollThreshold) {
            const carouselContext = controlRef.current.context;
            setIsIdle(false);
            if (maxDeltaX > 0) {
              if (carouselContext.state.currentSlide < carouselContext.state.totalSlides - 1) {
                controlRef.current.context.setStoreState({
                  currentSlide: carouselContext.state.currentSlide + 1,
                });
              }
            } else {
              if (carouselContext.state.currentSlide > 0) {
                controlRef.current.context.setStoreState({
                  currentSlide: carouselContext.state.currentSlide - 1,
                });
              }
            }
          }
          maxDeltaX = 0;
          setTimeout(() => {
            checkForScroll = true;
          }, 1000);
        }
      }
    };

    // modern Chrome requires { passive: false } when adding event
    let supportsPassive = false;
    try {
      carouselRef.current.addEventListener(
        'test',
        null,
        Object.defineProperty({}, 'passive', {
          get: function () {
            supportsPassive = true;
          },
        })
      );
    } catch (e) {}

    const wheelOpt = supportsPassive ? { passive: false } : false;
    const wheelEvent = 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';
    carouselRef.current.addEventListener(wheelEvent, handleScroll, wheelOpt); // modern desktop
    return () => {
      carouselRef.current.removeEventListener(wheelEvent, handleScroll);
    };
  }, []);

  useEffect(() => {
    if (controlRef.current) {
      const onChange = () => {
        setCurrentSlide(controlRef.current.context.state.currentSlide);
      };
      controlRef.current.context.subscribe(onChange);
      return () => controlRef.current.context.unsubscribe(onChange);
    }
  }, [controlRef.current]);

  const getSlideDataFromCard = card => {
    let config = {};
    const className = Object.keys(storyCardTypes)[card.Type].toLowerCase();
    switch (card.Type) {
      case storyCardTypes.TEXT_CAROUSEL:
        config = {
          ...card,
          className,
          staticContent: {
            Header: card.Name,
            Body: card.Body,
            subHeader: card.Header,
          },
          slides: card.SimpleTextCarousel.map(s => ({
            ...s,
          })),
        };
        break;
      case storyCardTypes.TEXT_CAROUSEL_WITH_BG:
        let staticHeader = true;
        if (card.SimpleTextCarousel[0].Header) {
          for (let i = 1; i < card.SimpleTextCarousel.length; i++) {
            if (card.SimpleTextCarousel[0].Header !== card.SimpleTextCarousel[i].Header) {
              staticHeader = false;
              break;
            }
          }
        } else {
          staticHeader = false;
        }
        config = {
          ...card,
          className,
          staticContent: {
            Header: staticHeader ? card.SimpleTextCarousel[0].Header : '',
            image: getBackground(card),
          },
          slides: card.SimpleTextCarousel.map(s => ({
            ...s,
            Header: staticHeader ? '' : s.Header
          })),
        };
        if (!staticHeader && card.StaticTitle) {
          config.staticContent.Header = card.Name;
          config.className += ' static-title';
        }
        break;
      case storyCardTypes.TEXT_IMAGE_CAROUSEL:
        config = {
          ...card,
          className,
          slides: card.ImageTextCarousel.map(s => ({ ...s })),
        };
        break;
      case storyCardTypes.TEXT_VIDEO_CAROUSEL:
        config = {
          ...card,
          className,
          staticContent: {
            Header: card.Header,
            Video: card.BackgroundVideo,
          },
          slides: card.SimpleTextCarousel.map(s => ({ ...s })),
        };
        if (card.Header) {
          config.className += ' static-title';
        }
        break;
      case storyCardTypes.MIXED_MEDIA_CAROUSEL:
        config = {
          ...card,
          className,
          slides: (card.MixedMediaCarousel || []).map(s => ({
            ...s,
            Header: s.header && !s.Header ? s.header : s.Header,
            Background: s.Media.Type === mixedMediaTypes.IMAGE ? getBackground(s.Media) : null,
            Video: s.Media.Type === mixedMediaTypes.VIDEO ? s.Media.Video : null,
          })),
        };
        break;
      case storyCardTypes.AUDIO_PODCAST:
        config = {
          ...card,
          className,

        }
        break;
      case storyCardTypes.AUDIO_MISC:
        config = {
          ...card,
          className,

        }
        break;
      default:
        break;
    }
    return config;
  };

  const getExternalLink = () => {
    var link = cardData && cardData.Link && cardData.Link.HasLink ? cardData.Link : null;

    if(cardData.slides){
      for(var i = 0; i<cardData.slides.length; i++){
        var slide = cardData.slides[i];
        const carouselContext = controlRef.current.context;
        if(slide && slide.Link && slide.Link.HasLink && 
          carouselContext.state.currentSlide === i){
            link = slide.Link;
        }
      }
    }
    
    if(link && link.Text && link.Text.length > 0 && cardData.Key >= 0){
      var isExternal = link.Type === linkTypes.EXTERNAL;
      return (
        <ExternalLink index={cardData.Key} text={link.Text} link={isExternal ? link.ExternalURL.Text : `/${link.InternalURL.BasePath && link.InternalURL.BasePath.Text}/${link.InternalURL.Path && link.InternalURL.Path.Path}`} isExternal={isExternal} isInline={isInline} />
      );
    } else {
      return null;
    }
  };

  

  const configData = useSelector(state => state.config);
  var externalLink = getExternalLink();

  var videoclass = 'background-video-holder ' + (loaded ? 'loaded' : '');

  //console.log("Is Scrolling? ", preventScrolling)
  return (
    <h2 className={`story-carousel ${cardData.className || ''} ${isInline ? 'inline' : ''}`} ref={carouselRef}>
      {cardData.staticContent && cardData.staticContent.Video ? (
        <div className={videoclass} role="img" alt={cardData.staticContent.Video.AltText}>
          <ReactPlayer
            ref={videoRef}
            className="background-video"
            width={dimensions.width + 'px'}
            height={dimensions.height + 'px'}
            playing={isActive || isInline}
            muted={true}
            volume={0}
            playsinline={true}
            onStart={handleVideoStart}
            url={cardData.staticContent.Video.URL}
            config={ cardData.staticContent && cardData.staticContent.Video.URL.indexOf("youtu.be") < 0 ? null : {
              youtube: {
                playerVars: {
                  autoplay:1,
                  showinfo: 0,
                },
              },
            }}
            loop={true}
          />
          <div className="touch-blocker" />
        </div>
      ) : null}
      <div
        className="static-content"
        role="img"
        alt={cardData.staticContent && cardData.staticContent.image ? cardData.staticContent.image.AltText : ""}
        style={
          cardData.staticContent &&
          cardData.staticContent.image &&
          cardData.staticContent.image.Image != null &&
          {
            backgroundImage: `url("${
              configData.basePath +
              configData.apiMediaPath +
              getImageFilepath(cardData.staticContent.image.Image)
            }")`,
          }
        }
      >
        {cardData.staticContent &&
        cardData.staticContent.image &&
        cardData.staticContent.image.Overlay && (
          <div
            className="overlay"
            style={{
              backgroundColor: cardData.staticContent.image.Overlay,
              opacity: cardData.staticContent.image.Opacity,
            }}
          ></div>
        )}
        {cardData.staticContent &&
        cardData.staticContent.Video &&
        cardData.staticContent.Video.Overlay && (
          <div
            className="overlay"
            style={{
              backgroundColor: cardData.staticContent.Video.Overlay,
              opacity: cardData.staticContent.Video.Opacity,
            }}
          ></div>
        )}
        {cardData.staticContent && cardData.staticContent.Header && (
          <div className="width-wrapper">
            <div className="static-content-wrapper">
              <h2 role={ADAARIARoles.Heading}>{cardData.staticContent.Header}</h2>
              {cardData.staticContent.Body ? (
                <div className="body">{formatParagraphs(cardData.staticContent.Body)}</div>
              ) : null}
              {cardData.staticContent.subHeader ? (
                <h3>{cardData.staticContent.subHeader}</h3>
              ) : null}
            </div>
          </div>
        )}
        {isInline && (!cardData.staticContent || (!cardData.staticContent.Header && !cardData.staticContent.Body)) &&
        ((cardData.Type === storyCardTypes.TEXT_CAROUSEL_WITH_BG) || 
        (cardData.Type === storyCardTypes.TEXT_VIDEO_CAROUSEL)) ? (
          <div className="width-wrapper">
            <div className="static-content-wrapper">
            </div>
          </div>
        ) : null}
        {externalLink}
      </div>

      <CarouselProvider //horizontal scrolling 
        totalSlides={(cardData.slides || []).length}
        naturalSlideWidth="100vw"
        naturalSlideHeight="100vh"
        dragStep={1}
        lockOnWindowScroll={true}
        onClick={handleCarouselInteraction}
        onTouchStart={(e) => {setTouchLocX(e.touches[0].clientX)}}
        onTouchMove={(e) => {if(Math.abs(e.touches[0].clientX - touchLocX ) >= 100){ //onScrollCallback(); 
                              } setLastTouchLocX(e.touches[0].clientX)}}//horizontal travel distance of at least 100px
        onTouchEnd={() => {
          if(!preventScrolling) {
            if(lastTouchLocX - touchLocX <= 100 && (controlRef.current.context.state.currentSlide < controlRef.current.context.state.totalSlides - 1) ){
              console.log("[StoryCarousel onTouchEnd] | x distance good for forwards")
              controlRef.current.context.setStoreState({
                currentSlide: controlRef.current.context.state.currentSlide + 1,
              });
            }
            else if(lastTouchLocX - touchLocX > 100 && controlRef.current.context.state.currentSlide > 0){
              console.log("[StoryCarousel onTouchEnd] | x distance good for backwards")
              controlRef.current.context.setStoreState({
                currentSlide: controlRef.current.context.state.currentSlide - 1,
              });
            }
          }
          else 
            console.log("!!!VERTICAL SCROLLING PREVENTS HORIZONTAL SCROLLING")
          onScrollCompleteCallback();
        }}
        dragEnabled= {false}//{!preventScrolling && (cardData.slides || []).length > 1}
        touchEnabled={false}//{!preventScrolling && (cardData.slides || []).length > 1}
        //style={{pointerEvents:preventScrolling ? 'none' : 'all'}}
        //isPlaying={isIdle && cardData.className !== 'mixed_media_carousel '}
        interval={10000}
      >
        

        <Slider classNameAnimation="carousel" moveThreshold={0.2}> 
          {(cardData.slides || []).map((slide, i) => {
            const slideBackground = getBackground(slide);
            return (
              <Slide key={slide.Key} index={i} >
                <div className={`img-holder ${slideBackground || slide.Video ? 'has-image' : ''} ${controlRef.current.context && i === controlRef.current.context.state.currentSlide ? "slide-active" : "slide-inactive" } ${externalLink ? "linkSlide" : ""}`}
                 
                  style={
                    slideBackground
                      ? {
                        //pointerEvents:preventScrolling ? 'none' : 'all',
                          backgroundImage: `url("${
                            configData.basePath +
                            configData.apiMediaPath +
                            getImageFilepath(slideBackground.Image)
                          }")`,
                        }
                      : {}
                  }
              
                >
                  {!forceInlineImage && slide.Video && size.width < 768 ? (
                    <div className="background-video-slide">
                      <VideoSlide
                        data={slide}
                        active={isActive && currentSlide === i && size.width < 768}
                        playOnActive={true}
                        closeButton={false}
                        forceShowControls={true}
                        showPlayButton={true}
                        canPlay={true}
                      />
                    </div>
                  ) : null}
                  {slideBackground && slideBackground.Overlay ? (
                    <div
                      className="overlay"
                      style={{
                        backgroundColor: slideBackground.Overlay,
                        opacity: slideBackground.Opacity,
                      }}
                    ></div>
                  ) : null}
                  <div className="content">
                    <div className="width-wrapper">
                      <div className="content-wrapper">
                        {slide.Header ? <h3 >{slide.Header}</h3> : null}
                        {cardData.className === 'text_carousel_with_bg' &&
                        cardData.staticContent &&
                        cardData.staticContent.Header ? (
                          <h2 className="static-transparent" >{cardData.staticContent.Header}</h2>
                        ) : null}
                        {forceInlineImage && slideBackground && slideBackground.Image ? <img className="inline-slide-image" src={`${configData.basePath + configData.apiMediaPath + getImageFilepath(slideBackground.Image)}`}/> : null}
                        {forceInlineImage && slide.Video && size.width < 768 ? (
                          <div className="video-holder">
                            <div className="background-video-slide">
                              <VideoSlide
                                data={slide}
                                active={isActive && currentSlide === i && size.width < 768}
                                playOnActive={true}
                                closeButton={false}
                                forceShowControls={true}
                                showPlayButton={true}
                                canPlay={true}
                              />
                            </div>
                          </div>
                        ) : null}
                        {slide.Subheader ? <h4 >{slide.Subheader}</h4> : null}
                        <div className={slide.Header ? 'body' : 'no-header'}>
                          {formatParagraphs(slide.Body)}
                        </div>
                      </div>
                    </div>
                    {isInline && slideBackground && slideBackground.Image && (
                      <img
                        className="carousel-img"
                        src={
                          configData.basePath +
                          configData.apiMediaPath +
                          getImageFilepath(slideBackground.Image)
                        }
                        alt={slideBackground.AltText}
                      />
                    )}
                    {slide.Video && (
                      <div className="carousel-img has-video">
                        {size.width >= 768 && (
                          <VideoSlide
                            data={slide}
                            active={isActive && currentSlide === i}
                            playOnActive={true}
                            forceShowControls={true}
                            closeButton={false}
                            canPlay={true}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Slide>
            );
          })}
        </Slider>

        <div className="btn-next-flexbox">
          <div className="btn-next-flexcontainer">
            <div className="btn-next-container">
              {currentSlide !== (cardData.slides || []).length - 1 && <ButtonNext onClick={() => { handleNextButtonPress() }} />}
            </div>
          </div>
        </div>
        
        <DotGroup
          ref={controlRef}
          style={{ visibility: (cardData.slides || []).length > 1 ? '' : 'hidden' }}
        />
      </CarouselProvider>
    </h2>
  );
};

export default StoryCarousel;
